import {useState, useEffect, useRef} from 'react'
import PostList from '../components/PostList'
import Pagination from '../components/Pagination'
import Filters from '../components/Filters'
import {buildRequestURL, parseWordpressPosts, queryParams} from '../lib/helpers'
import PostListSkeleton from "../components/PostListSkeleton";

const Blog = ({perPage, apiDomain, apiPath, customApiPath, paginationType}) => {

    const [filteredPosts, setFilteredPosts] = useState([])
    const [totalPosts, settotalPosts] = useState([])
    const [categories, setCategories] = useState([])
    const [authors, setAuthors] = useState([])
    const [sectors, setSectors] = useState([])
    const [years, setYears] = useState([])
    const [activePage, setActivePage] = useState(1)
    const [isLoadingPosts, setIsLoadingPosts] = useState(true)
    const [isLoadingMorePosts, setIsLoadingMorePosts] = useState(false)
    const [isLoadingCategories, setIsLoadingCategories] = useState(true)
    const [isLoadingAuthors, setIsLoadingAuthors] = useState(false)
    const [isLoadingSectors, setIsLoadingSectors] = useState(true)
    const [filters, setFilters] = useState({
        category: 0,
        author: 0,
        sector: 0,
        year: 0,
        search: ''
    })

    // Reference to main app div to avoid searching the DOM for it each render
    const appRef = useRef()

    const fetchPostsAppend = (args = {}) => {

        const url = buildRequestURL({
            apiDomain: apiDomain,
            apiPath: apiPath + '/posts',
            perPage: perPage,
            ...args,
        });

        fetch(url)
            .then(response => {
                if (typeof response.status !== 'undefined' && response.status === 200) {
                    settotalPosts(response.headers.get('X-WP-Total'));
                    return response.json();
                }
                throw response;
            })
            .then(rawData => parseWordpressPosts(rawData))
            .then(data => {
                // Append new posts to existing ones
                setFilteredPosts(prevPosts => [...prevPosts, ...data]);
            })
            .catch(error => {
                console.error('Error fetching posts', error);
            })
            .finally(() => {
                setIsLoadingPosts(false);
                setIsLoadingMorePosts(false);
            });
    };

    const fetchPosts = (args = {}) => {

        setIsLoadingPosts(true)

        const url = buildRequestURL({
            apiDomain: apiDomain,
            apiPath: apiPath + '/posts',
            perPage: perPage,
            ...args,
        })

        fetch(url)
            .then(response => {
                if (typeof response.status !== 'undefined' && response.status === 200) {
                    settotalPosts(response.headers.get('X-WP-Total'))
                    return response.json()
                }
                throw response
            })
            .then(rawData => {
                return parseWordpressPosts(rawData)
            })
            .then(data => {
                setFilteredPosts(data)
            })
            .catch(error => {
                console.error('Error fetching posts', error)
            })
            .finally(() => {
                setIsLoadingPosts(false)
            })
    }

    const fetchCategories = () => {

        const url = buildRequestURL({
            apiDomain: apiDomain,
            apiPath: apiPath + '/categories?per_page=100',
        })

        fetch(url)
            .then(response => {
                if (typeof response.status !== 'undefined' && response.status === 200) {
                    return response.json()
                }
                throw response
            })
            .then(data => {
                setCategories(data)

                // Extract slug from current path
                const pathParts = window.location.pathname.split('/').filter(Boolean);
                const maybeSlug = pathParts.length >= 2 ? pathParts[pathParts.length - 1] : null;

                if (maybeSlug) {
                    const matchedCategory = data.find(cat => cat.slug === maybeSlug);
                    if (matchedCategory) {
                        setFilters(prev => ({
                            ...prev,
                            category: matchedCategory.id
                        }));
                    }
                }
            })
            .catch(error => {
                console.error('Error fetching categories', error)
            })
            .finally(() => {
                setIsLoadingCategories(false)
            })
    }

    const fetchAuthors = () => {

        const url = buildRequestURL({
            apiDomain: apiDomain,
            apiPath: customApiPath + '/authors',
        })

        fetch(url)
            .then(response => {
                if (typeof response.status !== 'undefined' && response.status === 200) {
                    return response.json()
                }
                throw response
            })
            .then(data => {
                setAuthors(data)
            })
            .catch(error => {
                console.error('Error fetching authors', error)
            })
            .finally(() => {
                setIsLoadingAuthors(false)
            })
    }

    const fetchSectors = () => {

        const url = buildRequestURL({
            apiDomain: apiDomain,
            apiPath: apiPath + '/sector?per_page=100',
        })

        fetch(url)
            .then(response => {
                if (typeof response.status !== 'undefined' && response.status === 200) {
                    return response.json()
                }
                throw response
            })
            .then(data => {
                setSectors(data)
            })
            .catch(error => {
                console.error('Error fetching sectors', error)
            })
            .finally(() => {
                setIsLoadingSectors(false)
            })
    }

    const fetchYears = () => {
        const years = [2014];
        let year = years[0];
        const currentYear = new Date().getFullYear();

        while (year < currentYear) {
            year++;
            years.push(year)
        }
        setYears(years);
    }

    const updateFilter = (name, value) => {
        switch (name) {
            case 'blog-filters__category':
                setFilters((filters) => {
                    const intValue = parseInt(value)
                    return isNaN(intValue)
                        ? {...filters, category: 0}
                        : {...filters, category: parseInt(value)}
                })
                break;
            case 'blog-filters__author':
                setFilters((filters) => {
                    const intValue = parseInt(value)
                    return isNaN(intValue)
                        ? {...filters, author: 0}
                        : {...filters, author: parseInt(value)}
                })
                break;
            case 'blog-filters__sector':
                setFilters((filters) => {
                    const intValue = parseInt(value)
                    return isNaN(intValue)
                        ? {...filters, sector: 0}
                        : {...filters, sector: parseInt(value)}
                })
                break;
            case 'blog-filters__year':
                setFilters((filters) => {
                    const intValue = parseInt(value)
                    return isNaN(intValue)
                        ? {...filters, year: 0}
                        : {...filters, year: parseInt(value)}
                })
                break;
            // Can use memo to cache
            case "blog-filters__search":
                setFilters((filters) => {
                    return {...filters, search: value}
                })
        }
        setActivePage(1)
    }

    // Scroll to top of main app div

    const scrollToTop = () => {
        appRef.current.scrollIntoView({
            behavior: "smooth",
            block: "start"
        })
    }

    const handlePaginationButtonClick = (page) => {
        const pageStart = (page - 1) * perPage;

        if (paginationType === 'load_more') {
            setIsLoadingMorePosts(true)
            fetchPostsAppend({
                offset: pageStart,
                category: filters.category,
                search: filters.search,
                sector: filters.sector,
                author: filters.author,
                year: filters.year,
            });
        } else {
            fetchPosts({
                offset: pageStart,
                category: filters.category,
                search: filters.search,
                sector: filters.sector,
                author: filters.author,
                year: filters.year,
            });
            setTimeout(scrollToTop, 10);
        }

        setActivePage(page);
    };

    // Update initial category value when categories loaded

    useEffect(() => {
        if (!isLoadingCategories) {
            const initialCategory = categories.filter(category => {
                return category.slug === queryParams.category
            })
            if (typeof initialCategory[0] !== 'undefined' && initialCategory[0]) {
                updateFilter("blog-filters__category", initialCategory[0].id)
            }
        }
    }, [isLoadingCategories])

    useEffect(() => {
        if (!isLoadingAuthors) {
            const initialAuthor = authors.filter(author => {
                return author.slug === queryParams.author
            })
            if (typeof initialAuthor[0] !== 'undefined' && initialAuthor[0]) {
                updateFilter("blog-filters__author", initialAuthor[0].id)
            }
        }
    }, [isLoadingAuthors])

    useEffect(() => {
        if (typeof queryParams.s !== "undefined" && queryParams.s !== null) {
            updateFilter("blog-filters__search", queryParams.s)
        }
    }, [])

    // Update initial sector value when categories loaded

    useEffect(() => {
        if (!isLoadingSectors) {
            const initialSector = sectors.filter(sector => {
                return sector.slug === queryParams.sector
            })
            if (typeof initialSector[0] !== 'undefined' && initialSector[0]) {
                updateFilter("blog-filters__sector", initialSector[0].id)
            }
        }
    }, [isLoadingSectors])

    // Properly filter posts when filters change and on initial load

    useEffect(() => {
        fetchPosts({
            category: filters.category,
            search: filters.search,
            sector: filters.sector,
            author: filters.author,
            year: filters.year,
        })
    }, [filters])

    // Populate the filters

    useEffect(() => {
        fetchCategories()
        fetchSectors()
        fetchYears()
        fetchAuthors()
    }, [])

    return (
        <div>
            <div className="ls-react-blog-anchor" ref={appRef}/>

            {(isLoadingCategories || isLoadingSectors || isLoadingAuthors) && (
                <>
                    <div className="blog-filter">
                        <div><input className='form-control' type="text" disabled="disabled"/></div>
                        <div><input className='form-control' type="text" disabled="disabled"/></div>
                        <div><input className='form-control' type="text" disabled="disabled"/></div>
                        <div><input className='form-control' type="text" disabled="disabled"/></div>
                        <div><input className='form-control' type="text" disabled="disabled"/></div>
                    </div>
                </>
            )}
            {!isLoadingCategories && !isLoadingAuthors && !isLoadingSectors && (
                <>
                    <Filters
                        category={filters.category}
                        sector={filters.sector}
                        author={filters.author}
                        authors={authors}
                        search={filters.search}
                        categories={categories}
                        sectors={sectors}
                        years={years}
                        year={filters.year}
                        onFilterChange={updateFilter}
                    />
                </>
            )}
            {isLoadingPosts && (
                <>
                    <PostListSkeleton
                        perPage={perPage}
                    />
                </>
            )}
            {!isLoadingPosts && (
                <>
                    <PostList
                        posts={filteredPosts}
                        categories={categories}
                        updateFilter={updateFilter}
                    />
                    <Pagination
                        type={paginationType}
                        total={totalPosts}
                        perPage={perPage}
                        page={activePage}
                        isLoading={isLoadingMorePosts}
                        onButtonClick={handlePaginationButtonClick}
                    />
                </>
            )}
        </div>
    )
}

export default Blog